/**
 * Calendar Service
 * API client for unified calendar (Events + Tasks aggregation)
 */

import { apiClient } from './api-client'
import type {
  CalendarData,
  CalendarFilters,
  Event,
  CreateEventRequest
} from '../types/calendar'

/**
 * Calendar Service
 * Consumes /api/calendar (aggregated Events + Tasks)
 */
export const calendarService = {
  /**
   * Get unified calendar data (Events + Tasks)
   * ⭐ This endpoint applies ALL granular filters in the backend
   */
  async getCalendarData(filters?: CalendarFilters): Promise<CalendarData> {
    const params = new URLSearchParams()

    if (filters) {
      // Date range
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)

      // Type filters
      if (filters.itemTypes) {
        filters.itemTypes.forEach(type => params.append('itemTypes', type))
      }

      // Event-specific filters ⭐
      if (filters.eventTypes) {
        filters.eventTypes.forEach(type => params.append('eventTypes', type))
      }
      if (filters.eventStatuses) {
        filters.eventStatuses.forEach(status => params.append('eventStatuses', status))
      }

      // Task-specific filters ⭐
      if (filters.taskCategories) {
        filters.taskCategories.forEach(cat => params.append('taskCategories', cat))
      }
      if (filters.taskStatuses) {
        filters.taskStatuses.forEach(status => params.append('taskStatuses', status))
      }

      // Common filters ⭐
      if (filters.priorities) {
        filters.priorities.forEach(priority => params.append('priorities', priority))
      }

      // Relationships
      if (filters.clientId) params.append('clientId', filters.clientId)
      if (filters.caseId) params.append('caseId', filters.caseId)
      if (filters.userId) params.append('userId', filters.userId)

      // Search
      if (filters.search) params.append('search', filters.search)
      if (filters.tags) {
        filters.tags.forEach(tag => params.append('tags', tag))
      }

      // Special filters
      if (filters.upcomingOnly) params.append('upcomingOnly', 'true')
      if (filters.overdueOnly) params.append('overdueOnly', 'true')
      if (filters.needsActionOnly) params.append('needsActionOnly', 'true')

      // Pagination
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.sortBy) params.append('sortBy', filters.sortBy)
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
    }

    const url = `/api/calendar?${params.toString()}`
    console.log('Calendar API request:', url)

    const response = await apiClient.get<{
      success: boolean
      data: CalendarData['items']
      pagination: CalendarData['pagination']
      stats: CalendarData['stats']
    }>(`/api/calendar?${params.toString()}`)

    return {
      items: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats
    }
  },

  /**
   * Get daily calendar view
   */
  async getDailyView(date: string, filters?: Omit<CalendarFilters, 'startDate' | 'endDate'>): Promise<CalendarData> {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.clientId) params.append('clientId', filters.clientId)
      if (filters.caseId) params.append('caseId', filters.caseId)
      if (filters.userId) params.append('userId', filters.userId)
      if (filters.itemTypes) {
        filters.itemTypes.forEach(type => params.append('itemTypes', type))
      }
    }

    const response = await apiClient.get<{
      success: boolean
      data: CalendarData['items']
      pagination: CalendarData['pagination']
      stats: CalendarData['stats']
    }>(`/api/calendar/daily/${date}?${params.toString()}`)

    return {
      items: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats
    }
  },

  /**
   * Get weekly calendar view
   */
  async getWeeklyView(weekStart: string, filters?: Omit<CalendarFilters, 'startDate' | 'endDate'>): Promise<CalendarData> {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.clientId) params.append('clientId', filters.clientId)
      if (filters.caseId) params.append('caseId', filters.caseId)
      if (filters.userId) params.append('userId', filters.userId)
      if (filters.itemTypes) {
        filters.itemTypes.forEach(type => params.append('itemTypes', type))
      }
    }

    const response = await apiClient.get<{
      success: boolean
      data: CalendarData['items']
      pagination: CalendarData['pagination']
      stats: CalendarData['stats']
    }>(`/api/calendar/weekly/${weekStart}?${params.toString()}`)

    return {
      items: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats
    }
  },

  /**
   * Get monthly calendar view
   */
  async getMonthlyView(year: number, month: number, filters?: Omit<CalendarFilters, 'startDate' | 'endDate'>): Promise<CalendarData> {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.clientId) params.append('clientId', filters.clientId)
      if (filters.caseId) params.append('caseId', filters.caseId)
      if (filters.userId) params.append('userId', filters.userId)
      if (filters.itemTypes) {
        filters.itemTypes.forEach(type => params.append('itemTypes', type))
      }
    }

    const response = await apiClient.get<{
      success: boolean
      data: CalendarData['items']
      pagination: CalendarData['pagination']
      stats: CalendarData['stats']
    }>(`/api/calendar/monthly/${year}/${month}?${params.toString()}`)

    return {
      items: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats
    }
  }
}

/**
 * Event Service
 * Direct event CRUD operations (for creating/editing events)
 */
export const eventService = {
  /**
   * Create a new event
   */
  async create(data: CreateEventRequest): Promise<Event> {
    const response = await apiClient.post<{
      success: boolean
      data: Event
    }>('/api/events', data)

    return response.data.data
  },

  /**
   * Get event by ID
   */
  async getById(id: string): Promise<Event> {
    const response = await apiClient.get<{
      success: boolean
      data: Event
    }>(`/api/events/${id}`)

    return response.data.data
  },

  /**
   * Update event
   */
  async update(id: string, data: Partial<CreateEventRequest>): Promise<Event> {
    const response = await apiClient.put<{
      success: boolean
      data: Event
    }>(`/api/events/${id}`, data)

    return response.data.data
  },

  /**
   * Delete event
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/events/${id}`)
  },

  /**
   * Get event statistics
   */
  async getStats(filters?: {
    clientId?: string
    caseId?: string
    startDate?: string
    endDate?: string
  }): Promise<Record<string, unknown>> {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.clientId) params.append('clientId', filters.clientId)
      if (filters.caseId) params.append('caseId', filters.caseId)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)
    }

    const response = await apiClient.get<{
      success: boolean
      data: Record<string, unknown>
    }>(`/api/events/stats?${params.toString()}`)

    return response.data.data
  }
}
